/**
 * Solana RPC and transaction error formatting utilities.
 *
 * Converts technical Solana error messages into user-friendly descriptions.
 */

interface SolanaErrorInfo {
  type:
    | "rpc_timeout"
    | "blockhash"
    | "confirmation"
    | "insufficient_funds"
    | "user_rejected"
    | "unknown";
  userMessage: string;
  recoverable: boolean;
}

const ERROR_PATTERNS: Array<[string, SolanaErrorInfo]> = [
  // RPC timeout errors
  [
    "gateway_timeout",
    {
      type: "rpc_timeout",
      userMessage: "Network is congested. Please try again.",
      recoverable: true,
    },
  ],
  [
    "rpc request timeout",
    {
      type: "rpc_timeout",
      userMessage: "Request timed out. Network may be busy.",
      recoverable: true,
    },
  ],
  [
    "504",
    {
      type: "rpc_timeout",
      userMessage: "Network is congested. Please try again.",
      recoverable: true,
    },
  ],

  // Blockhash errors
  [
    "failed to get recent blockhash",
    {
      type: "blockhash",
      userMessage: "Unable to connect to Solana network. Please try again.",
      recoverable: true,
    },
  ],
  [
    "blockhash not found",
    {
      type: "blockhash",
      userMessage: "Transaction expired. Please try again.",
      recoverable: true,
    },
  ],
  [
    "block height exceeded",
    {
      type: "blockhash",
      userMessage: "Transaction expired. Please try again.",
      recoverable: true,
    },
  ],

  // Transaction simulation errors
  [
    "transaction simulation failed",
    {
      type: "confirmation",
      userMessage: "Transaction simulation failed. Please check your inputs.",
      recoverable: true,
    },
  ],
  [
    "transaction was not confirmed",
    {
      type: "confirmation",
      userMessage:
        "Transaction was not confirmed. It may still succeed, please check your wallet.",
      recoverable: true,
    },
  ],

  // Insufficient funds
  [
    "insufficient funds",
    {
      type: "insufficient_funds",
      userMessage: "Insufficient balance to complete this transaction.",
      recoverable: false,
    },
  ],
  [
    "insufficient lamports",
    {
      type: "insufficient_funds",
      userMessage: "Insufficient SOL balance for transaction fees.",
      recoverable: false,
    },
  ],

  // User rejection
  [
    "user rejected",
    {
      type: "user_rejected",
      userMessage: "Transaction was cancelled.",
      recoverable: true,
    },
  ],
  [
    "user declined",
    {
      type: "user_rejected",
      userMessage: "Transaction was cancelled.",
      recoverable: true,
    },
  ],
  [
    "transaction cancelled",
    {
      type: "user_rejected",
      userMessage: "Transaction was cancelled.",
      recoverable: true,
    },
  ],
];

/**
 * Extracts error message from various error formats.
 */
function extractErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object") {
    if ("message" in error) {
      return String((error as { message: unknown }).message);
    }
    // Handle nested error objects (common in RPC responses)
    try {
      return JSON.stringify(error);
    } catch {
      return "Unknown error";
    }
  }
  return "Unknown error";
}

/**
 * Formats a Solana RPC or transaction error into a user-friendly message.
 *
 * @param error - The error object (Error, string, or unknown)
 * @returns A user-friendly error message
 *
 * @example
 * ```typescript
 * try {
 *   await sendTransaction(tx);
 * } catch (error) {
 *   toast.error("Transaction failed", {
 *     description: formatSolanaError(error),
 *   });
 * }
 * ```
 */
export function formatSolanaError(error: unknown): string {
  const message = extractErrorMessage(error);
  const lowerMessage = message.toLowerCase();

  for (const [pattern, info] of ERROR_PATTERNS) {
    if (lowerMessage.includes(pattern.toLowerCase())) {
      return info.userMessage;
    }
  }

  // Generic fallback - hide technical details
  return "Transaction failed. Please try again.";
}

/**
 * Gets detailed error information for programmatic handling.
 *
 * @param error - The error object
 * @returns Detailed error info including type and recoverability
 */
export function getSolanaErrorInfo(error: unknown): SolanaErrorInfo {
  const message = extractErrorMessage(error);
  const lowerMessage = message.toLowerCase();

  for (const [pattern, info] of ERROR_PATTERNS) {
    if (lowerMessage.includes(pattern.toLowerCase())) {
      return info;
    }
  }

  return {
    type: "unknown",
    userMessage: "Transaction failed. Please try again.",
    recoverable: true,
  };
}
